import { useCallback, useEffect, useRef } from 'react'

import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { initWs } from 'shared/websocket/ws'
import {
  addNewAssetThunk,
  addNewAssetWithUpdatedFieldsThunk,
  assetsActions,
  assetsSelectors,
  fetchNodeById } from 'features/assets/model/slice/assetsSlice'
import { boardsActions } from 'features/boards/model/slice/boardsSlice'
import { NodeTypes } from '@cloudike/web_files'

import { filesPreviewActions } from '../../files-preview/model/slice/filesPreviewSlice'
import { WSActions } from '../../../shared/constants/common'

type Fs2Change = {
    id: string | number
    state?: 'in_process' | 'done' | string
    action?: 'changed' | 'created' | string
    node_id: string
    type: NodeTypes
    fields?: Record<string, any>
    [key: string]: any
  }

type Fs2Message = {
  output?: { changes?: Fs2Change[] }
  group?: string
}

const createNewBoardItem = (itemChange: Fs2Change, fields: Record<string, unknown>) => {
  const name = itemChange?.name
  const id = itemChange?.node_id
  const authorName = itemChange?.author?.name
  const authorId = itemChange?.author?.id
  const parentId = itemChange?.fields?.parent_id ?? itemChange?.parent_id
  return {
    id,
    name,
    type: NodeTypes.DIR,
    author_name: authorName,
    author_id: authorId,
    parent_id: parentId,
    is_trashed: false,
    is_explicitly_trashed: false,
    description: '',
    is_shared: false,
    ...fields,
  }
}

const isAssetType = (type: NodeTypes) => type === NodeTypes.FILE || type === NodeTypes.LINK

export const useBoardsAssetsWS = () => {
  const dispatch = useAppDispatch()
  const boardAssets = useAppSelector(state => assetsSelectors.selectAll(state.assets))
  const boardAssetsRef = useRef(boardAssets)
  const inFlightAdditionsRef = useRef<Set<string>>(new Set())
  const pendingChangesRef = useRef<Map<string | number, any>>(new Map())

  useEffect(() => {
    boardAssetsRef.current = boardAssets
  }, [boardAssets])

  const updateItemByType = useCallback((nodeId: string, fields: any, type: NodeTypes) => {
    const isProbablyVersionChange = !!fields?.file_info?.size || !!fields?.file_info?.checksum
    const assets = boardAssetsRef.current ?? []
    let itemId = nodeId
    if (isAssetType(type)) {
      const exists = assets.some((asset: any) => asset?.id === itemId || asset?.linked_node_id === itemId)
      if (!exists && !inFlightAdditionsRef.current.has(itemId)) {
        inFlightAdditionsRef.current.add(itemId)
        dispatch(addNewAssetWithUpdatedFieldsThunk({ nodeId: itemId, fields }))
          .finally(() => inFlightAdditionsRef.current.delete(itemId))
      }
      const link = assets.find((asset: any) => asset?.linked_node_id === itemId)
      itemId = link?.id ?? itemId

      if (isProbablyVersionChange) {
        // if it's version change, we need to fetch the node to get the latest data
        dispatch(fetchNodeById({ nodeId: nodeId })).unwrap().then((node) => {
          dispatch(assetsActions.updateAsset({ id: itemId, ...node }))
          dispatch(filesPreviewActions.updateItem({ id: itemId, ...node }))
        })

        return
      }

      dispatch(assetsActions.updateAsset({ id: itemId, ...fields }))
      dispatch(filesPreviewActions.updateItem({ id: itemId, ...fields }))

      return
    }

    if (type === NodeTypes.DIR) {
      dispatch(boardsActions.updateItem({ id: nodeId, ...fields }))
    }

  }, [dispatch])

  const addNewItemByType = useCallback((itemChange: Fs2Change, fields: any, type: NodeTypes) => {

    const itemId = itemChange?.node_id
    if (isAssetType(type)) {
      if (!inFlightAdditionsRef.current.has(itemId)) {
        inFlightAdditionsRef.current.add(itemId)
        dispatch(addNewAssetThunk({ nodeId: itemId }))
          .finally(() => inFlightAdditionsRef.current.delete(itemId))
      }
      return
    }

    if (type === NodeTypes.DIR) {
      const newItem = createNewBoardItem(itemChange, fields)
      dispatch(boardsActions.addItem(newItem))
    }
  }, [dispatch])

  const processCompletedChange = useCallback((change: Fs2Change) => {
    const { action, node_id, fields = {}, type } = change

    if (action === WSActions.CHANGED) {
      updateItemByType(node_id, fields, type)
      return
    }

    if (action === WSActions.CREATED) {
      addNewItemByType(change, fields, type)
    }
  }, [addNewItemByType, updateItemByType])

  useEffect(() => {
    const ws = initWs({ includeFamilyUserId: true })

    const handleBoardAssetsChanges = (message: Fs2Message) => {
      const { output, group } = message
      if (group !== 'fs2' || !output?.changes?.length) return
      output.changes.forEach((change) => {
        if (change.state === 'in_process') {
          pendingChangesRef.current.set(change.id, output)
          return
        }

        if (change.state === 'done') {
          const pendingChange = pendingChangesRef.current.get(change.id)?.changes?.[0]
          processCompletedChange(pendingChange || change)
          pendingChangesRef.current.delete(change.id)
        }
      })
    }

    ws.addEventListener('fs2', handleBoardAssetsChanges)

    return () => {
      ws.removeEventListener('fs2', handleBoardAssetsChanges)
    }
  }, [processCompletedChange])
}
